'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { HelpCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';

interface GameRulesModalProps {
  gameTitle?: string;
  isOpen?: boolean;
  onClose?: () => void;
  children?: React.ReactNode;
  rulesContent: React.ReactNode;
}

/**
 * Универсальное модальное окно с правилами игры
 * Может использоваться во всех играх платформы
 */
export function GameRulesModal({ 
  gameTitle, 
  isOpen, 
  onClose, 
  children, 
  rulesContent 
}: GameRulesModalProps) {
  const t = useTranslations();

  if (isOpen !== undefined) {
    // Управляемый режим
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose?.()}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="game-rules-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" data-testid="game-rules-title">
              <HelpCircle className="h-5 w-5 text-primary" />
              {gameTitle || t('games.rules')} - {t('games.rules')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4" data-testid="game-rules-body">
            {rulesContent}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Неуправляемый режим (с триггером)
  return (
    <Dialog>
      <DialogTrigger asChild>
        {children || (
          <Button type="button" variant="outline" size="sm" className="gap-2" data-testid="game-rules-trigger-button">
            <HelpCircle className="h-4 w-4" />
            {t('games.rules')}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="game-rules-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" data-testid="game-rules-title">
            <HelpCircle className="h-5 w-5 text-primary" />
            {gameTitle || t('games.rules')} - {t('games.rules')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4" data-testid="game-rules-body">
          {rulesContent}
        </div>
      </DialogContent>
    </Dialog>
  );
}
